import React, { useEffect, useRef, useState } from "react"
import { Alert, Badge, Button, Card, CardBody, Col, Container, FormFeedback, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap"
import { get } from "helpers/api_helper"
import Breadcrumbs from "components/Common/Breadcrumb"
import useLogisticsOperation from "pages/EventsManagement/components/Tabs/Logistics/useLogisticsOperation"
import PackageDialog from "./PackageDialog"
import { ENVELOPE_WEIGHT_LIMIT, FIELD_LIMIT, LINK_LIMIT, PACKAGE_NAME_LIMIT, SIDE_LIMIT, TEAM_LANGUAGES, TEAM_NAME_LIMIT, TRACKING_LANGUAGES, WEIGHT_LIMIT,
  canBeDefault, contactErrors, contactForm, contactOf, dimensionsLabel, packageErrors, packageForm, packageOf, sameContact, samePackage, sameTracking,
  trackingErrors, trackingForm, trackingOf, typeLabel, weightLabel } from "./settingsForm"
import "pages/EventsManagement/components/Tabs/Logistics/logistics.scss"

const UNCONFIRMED = "The settings could not confirm this change. Reload the page before trying again."
const messages = {
  LOGISTICS_PACKAGE_IS_DEFAULT: "The default package stays active. Make another package the default first.",
  LOGISTICS_PACKAGE_INACTIVE: "An inactive package cannot be the default. Activate it first.",
  LOGISTICS_PACKAGE_ENVELOPE: "The default package is a box: an envelope carries only documents.",
  LOGISTICS_PACKAGE_NAME_TAKEN: "Another package has this name.",
  LOGISTICS_PACKAGE_NOT_FOUND: "This package is no longer in the settings. Reload the page.",
  INVALID_LOGISTICS_PACKAGE: `Check the package: a name of up to ${PACKAGE_NAME_LIMIT} characters, whole sides from 1 to ${SIDE_LIMIT} cm and an own `
    + `weight from 0 to ${WEIGHT_LIMIT} kg; an envelope has no height and weighs at most ${ENVELOPE_WEIGHT_LIMIT} kg.`,
  INVALID_LOGISTICS_TEAM_CONTACT: "Enter the team's name in the three languages, a phone number and a valid email.",
  INVALID_LOGISTICS_TRACKING_LINK: "Each link starts with https:// and contains {awb} once, after the site's address.",
  ERROR_PERMISSIONS: "Only an active SuperAdmin can change the settings.",
  SUPER_ADMIN_REQUIRED: "Only an active SuperAdmin can change the settings.",
  OPERATION_PAYLOAD_CONFLICT: "This request belongs to a different change. Reload before trying again.",
}
export const settingsMessage = code => messages[code] || UNCONFIRMED
const SAVED = { createPackage: "Package added and logged.", updatePackage: "Package saved and logged.",
  setDefaultPackage: "Default package changed and logged.", setTeamContact: "Contact saved and logged.", setCargusTracking: "Template saved and logged." }
// Where a refusal is shown: in the package dialog, or on the card whose change it refuses.
const PLACES = { createPackage: "package", updatePackage: "package", setDefaultPackage: "packages", setTeamContact: "contact",
  setCargusTracking: "tracking" }

// A card of the page, named by its title for assistive technology, with the refusal of its last change.
const Section = ({ title, note, action, failure, children }) => <Card tag="section" aria-label={title}><CardBody>
  <div className="d-flex flex-wrap align-items-start justify-content-between">
    <div className="mr-2">
      <h4 className="card-title mb-1">{title}</h4>
      <p className="text-muted small">{note}</p>
    </div>
    {action}
  </div>
  {children}
  {failure && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
</CardBody></Card>

// Logistics → Settings (US 144, ticket 22 Q4): the shipping packages, the Sportya logistics team's contact
// and the Cargus tracking link. Every change runs as a command and is logged with its before and after.
export function LogisticsSettings() {
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState({ loading: true })
  // The contact and link cards' forms, each with the errors of its last check.
  const [contact, setContact] = useState({ form: contactForm(null), errors: {} })
  const [tracking, setTracking] = useState({ form: trackingForm(null), errors: {} })
  // The package being added or edited ({ packageId, name, form, errors }).
  const [editing, setEditing] = useState(null)
  const [failure, setFailure] = useState({})
  const [notice, setNotice] = useState("")
  const operation = useLogisticsOperation(undefined, settingsMessage)
  const loaded = useRef(null)

  useEffect(() => {
    let current = true
    get("/admin/logistics/settings")
      .then(response => {
        if (!current) return
        const settings = response?.data
        // A card takes what is saved unless it holds changes not saved yet.
        const previous = loaded.current
        loaded.current = settings
        setState({ settings })
        setContact(card => sameContact(card.form, previous?.teamContact) || sameContact(card.form, settings.teamContact)
          ? { form: contactForm(settings.teamContact), errors: {} } : card)
        setTracking(card => sameTracking(card.form, previous?.cargusTracking) || sameTracking(card.form, settings.cargusTracking)
          ? { form: trackingForm(settings.cargusTracking), errors: {} } : card)
      })
      .catch(() => current && setState({ error: "The settings could not be loaded." }))
    return () => { current = false }
  }, [attempt])

  const settings = state.settings
  // Runs one change of the settings. A saved one reloads the settings; one that cannot be confirmed
  // reloads them too, so the page shows what Events kept.
  const apply = async (action, body) => {
    setFailure({})
    setNotice("")
    const result = await operation.run(action, "/admin/logistics/settings", { action, ...body })
    if (!result || result.status === "cancelled") return false
    if (result.status !== "error") setAttempt(value => value + 1)
    if (result.status !== "saved") {
      setFailure({ [PLACES[action]]: result.message || UNCONFIRMED })
      return false
    }
    setNotice(SAVED[action])
    return true
  }

  const openPackage = shippingPackage => {
    setFailure({})
    setEditing({ packageId: shippingPackage?.id ?? null, name: shippingPackage?.name ?? "", form: packageForm(shippingPackage) })
  }
  // The package the dialog edits, as the page last read it.
  const edited = editing?.packageId ? settings?.packages.find(({ id }) => id === editing.packageId) : null
  const savePackage = async () => {
    const errors = packageErrors(editing.form, settings.packages, editing.packageId)
    setEditing({ ...editing, errors })
    if (Object.keys(errors).length) return
    const saved = editing.packageId ? await apply("updatePackage", { packageId: editing.packageId, package: packageOf(editing.form) })
      : await apply("createPackage", { package: packageOf(editing.form) })
    if (saved) setEditing(null)
  }
  // A card checks its form, then saves it whole.
  const saveCard = (card, setCard, check, action, field, valueOf) => () => {
    const errors = check(card.form)
    setCard({ ...card, errors })
    if (!Object.keys(errors).length) apply(action, { [field]: valueOf(card.form) })
  }

  const input = (card, setCard, prefix) => (field, props = {}) => <>
    <Input id={`${prefix}-${field}`} value={card.form[field]} invalid={Boolean(card.errors[field])}
      onChange={event => setCard({ ...card, form: { ...card.form, [field]: event.target.value } })} {...props} />
    {card.errors[field] && <FormFeedback>{card.errors[field]}</FormFeedback>}
  </>
  const contactInput = input(contact, setContact, "logistics-contact")
  const trackingInput = input(tracking, setTracking, "logistics-tracking")

  return <div className="page-content logistics-settings">
    <Container fluid>
      <Breadcrumbs title="Logistics" breadcrumbItem="Settings" path="/logistics/settings" />
      {notice && <Alert color="success" isOpen toggle={() => setNotice("")}>{notice}</Alert>}
      {state.loading && <div className="text-center my-4"><Spinner color="primary" /></div>}
      {state.error && <Alert color="danger">{state.error}{" "}
        <Button color="link" className="p-0 align-baseline" onClick={() => { setState({ loading: true }); setAttempt(value => value + 1) }}>Retry</Button>
      </Alert>}
      {settings && <Row>
        <Col xl={7}>
          <Section title="Shipping packages" note="Used to split a shipment into parcels and to calculate its weight. One is the default."
            failure={failure.packages} action={<Button color="primary" size="sm" onClick={() => openPackage(null)} disabled={operation.pending}>
              <i className="bx bx-plus mr-1" aria-hidden="true" />Add package</Button>}>
            {settings.packages.length ? <Table className="logistics-stack mb-0">
              <thead><tr><th>Name</th><th>Dimensions</th><th className="text-right">Own weight</th><th>Default</th></tr></thead>
              <tbody>{settings.packages.map(shippingPackage => <tr key={shippingPackage.id} className={shippingPackage.active ? "" : "logistics-inactive"}>
                <td>
                  <Button color="link" className="p-0 text-left font-weight-medium" onClick={() => openPackage(shippingPackage)}>{shippingPackage.name}</Button>
                  <div className="small text-muted">{typeLabel(shippingPackage)}
                    {!shippingPackage.active && <> · <Badge className="badge-soft-secondary">Inactive</Badge></>}</div>
                </td>
                <td data-label="Dimensions">{dimensionsLabel(shippingPackage)}</td>
                <td className="text-right" data-label="Own weight">{weightLabel(shippingPackage)}</td>
                <td data-label="Default">
                  <Input type="radio" className="position-static ml-0" name="logistics-default-package" aria-label={`Default: ${shippingPackage.name}`}
                    checked={shippingPackage.default} disabled={!canBeDefault(shippingPackage) || operation.pending}
                    onChange={() => apply("setDefaultPackage", { packageId: shippingPackage.id })} />
                </td>
              </tr>)}</tbody>
            </Table> : <p className="text-muted mb-0">No shipping packages yet.</p>}
          </Section>
        </Col>
        <Col xl={5}>
          <Section title="Sportya logistics contact" note="Shown to organizers and clubs in SportyaOS, on the printed sheet and in the shipment emails."
            failure={failure.contact}>
            <FormGroup>
              <Label for="logistics-contact-ro">Team name · RO / EN / BG</Label>
              {TEAM_LANGUAGES.map(([language, name]) => <div key={language} className="mb-1">
                {contactInput(language, { "aria-label": `Team name in ${name}`, maxLength: TEAM_NAME_LIMIT })}
              </div>)}
            </FormGroup>
            <Row form>
              <Col sm={6}><FormGroup><Label for="logistics-contact-phone">Phone</Label>{contactInput("phone", { maxLength: FIELD_LIMIT })}</FormGroup></Col>
              <Col sm={6}><FormGroup><Label for="logistics-contact-email">Email</Label>
                {contactInput("email", { type: "email", maxLength: FIELD_LIMIT })}</FormGroup></Col>
            </Row>
            <Button color="primary" size="sm" disabled={operation.pending || sameContact(contact.form, settings.teamContact)}
              onClick={saveCard(contact, setContact, contactErrors, "setTeamContact", "teamContact", contactOf)}>Save</Button>
          </Section>
          <Section title="Cargus tracking link" note={"Template for the link sent to clubs; {awb} is replaced by the AWB number."} failure={failure.tracking}>
            {TRACKING_LANGUAGES.map(([field, label]) => <FormGroup key={field} className="mb-2">
              <Label for={`logistics-tracking-${field}`}>{label}</Label>
              {trackingInput(field, { maxLength: LINK_LIMIT, placeholder: "https://…{awb}" })}
            </FormGroup>)}
            <Button color="primary" size="sm" disabled={operation.pending || sameTracking(tracking.form, settings.cargusTracking)}
              onClick={saveCard(tracking, setTracking, trackingErrors, "setCargusTracking", "cargusTracking", trackingOf)}>Save</Button>
          </Section>
        </Col>
      </Row>}
    </Container>
    {editing && <PackageDialog editing={editing} isDefault={Boolean(edited?.default)} unchanged={samePackage(editing.form, edited)} pending={operation.pending}
      failure={failure.package} onChange={form => setEditing({ ...editing, form })} onSave={savePackage} onClose={() => { setEditing(null); setFailure({}) }} />}
  </div>
}

export default LogisticsSettings
